import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  CalendarToday,
  Repeat,
  Schedule,
} from '@mui/icons-material';
import {
  formatHours,
  formatOperationalDays,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  MotionList,
  MotionItem,
} from './themeHelpers';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function MinimalTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `A quiet space for focused work and study.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);

  // Seat display
  const availSeats = library.available_seats ?? library.capacity;
  const seatLabel =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity} seats`
      : library.capacity
      ? `${library.capacity} seats`
      : null;

  // Key info columns: location, capacity, hours, optionally lockers
  const showLockers = library.locker_count > 0;
  const infoCols = showLockers ? 3 : 4; // xs col width: 3 = 4 items, 4 = 3 items

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
      {/* Navigation */}
      <Box sx={{ py: 3, borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 300, letterSpacing: 2 }}>
              {library.name}
            </Typography>
            <Button
              onClick={() => onBookNow()}
              sx={{
                color: accentColor,
                fontWeight: 400,
                transition: 'opacity 0.2s',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline', opacity: 0.75 },
              }}
            >
              Book
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero - Full width image */}
      <Box sx={{ position: 'relative', height: { xs: '60vh', md: '80vh' } }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'white',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}
      </Box>

      {/* Introduction — subtle fade on scroll */}
      <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
        <Reveal y={16} duration={0.6}>
          <Typography variant="h2" sx={{ fontWeight: 300, mb: 4, letterSpacing: -1 }}>
            {library.name}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', lineHeight: 2, fontSize: '1.1rem' }}
          >
            {library.description || defaultDescription}
          </Typography>
        </Reveal>
      </Container>

      {/* Key Info */}
      <Box sx={{ borderTop: '1px solid #eee', borderBottom: '1px solid #eee', py: 8 }}>
        <Container maxWidth="lg">
          <Reveal y={12} duration={0.5}>
            <Grid container spacing={6}>
              {/* Location */}
              <Grid item xs={12} md={infoCols}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                  Location
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                  {library.address}
                </Typography>
              </Grid>

              {/* Capacity / available */}
              {seatLabel && (
                <Grid item xs={12} md={infoCols}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                    {library.available_seats !== null && library.available_seats !== undefined
                      ? 'Available'
                      : 'Capacity'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                    {seatLabel}
                  </Typography>
                </Grid>
              )}

              {/* Hours */}
              <Grid item xs={12} md={infoCols}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                  Hours
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                  {hoursStr || 'Contact for hours'}
                </Typography>
                {daysStr && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 300, mt: 0.5 }}>
                    {daysStr}
                  </Typography>
                )}
              </Grid>

              {/* Lockers (optional 4th column) */}
              {showLockers && (
                <Grid item xs={12} md={infoCols}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                    Lockers
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 400, mt: 1 }}>
                    {library.locker_count} available
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Reveal>
        </Container>
      </Box>

      {/* Plans — subtle staggered fade-in */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Reveal y={12} duration={0.5}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, display: 'block', mb: 4 }}>
            Membership
          </Typography>
        </Reveal>

        {plans.length > 0 ? (
          <MotionList
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 24,
            }}
          >
            {plans.map((plan) => {
              const planName = plan.name || titleCasePlanType(plan.plan_type);
              const price = formatPrice(plan.price_cents);
              const period = planPeriodLabel(plan.plan_type);
              const discPct = plan.first_time_discount;
              const discPrice = discountedFirstPrice(plan.price_cents, discPct);

              return (
                <MotionItem key={plan.id}>
                  <Box
                    sx={{
                      p: 4,
                      border: '1px solid #eee',
                      textAlign: 'center',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        borderColor: accentColor,
                        boxShadow: `0 4px 20px ${accentColor}18`,
                      },
                    }}
                  >
                    {/* Plan name */}
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                      {planName}
                    </Typography>

                    {/* Price — subtle discount treatment (no loud badge) */}
                    {price && (
                      <>
                        {discPct > 0 && discPrice !== null ? (
                          <Box sx={{ my: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.disabled', textDecoration: 'line-through', fontWeight: 300 }}
                            >
                              {price}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 300 }}>
                              {formatPrice(discPrice)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 300,
                                borderBottom: `1px solid ${accentColor}`,
                                pb: 0.25,
                              }}
                            >
                              {discPct}% off, first time only
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="h3" sx={{ fontWeight: 300, my: 2 }}>
                            {price}
                          </Typography>
                        )}
                        {period && (
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontWeight: 300, mb: 1.5 }}
                          >
                            {period}
                          </Typography>
                        )}
                      </>
                    )}

                    {/* Feature rows — tiny muted captions */}
                    <Box sx={{ textAlign: 'left', mb: 2 }}>
                      {plan.duration_days && (
                        <PlanFeature icon={<CalendarToday />} color="text.disabled">
                          Valid {plan.duration_days} days
                        </PlanFeature>
                      )}
                      <PlanFeature icon={<Schedule />} color="text.disabled">
                        {hoursLabel(plan.hours_per_day)}
                      </PlanFeature>
                      {plan.recurring && (
                        <PlanFeature icon={<Repeat />} color="text.disabled">
                          Auto-renews
                        </PlanFeature>
                      )}
                    </Box>

                    {/* Description (fine print) */}
                    {plan.description ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontWeight: 300,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                        }}
                      >
                        {plan.description}
                      </Typography>
                    ) : null}

                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => onBookNow(plan)}
                      sx={{
                        color: accentColor,
                        fontWeight: 400,
                        transition: 'opacity 0.2s',
                        '&:hover': { bgcolor: 'transparent', opacity: 0.7, textDecoration: 'underline' },
                      }}
                    >
                      Select
                    </Button>
                  </Box>
                </MotionItem>
              );
            })}
          </MotionList>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 3 }}>
              Contact us for pricing
            </Typography>
            <Button onClick={() => onBookNow()} sx={{ color: accentColor }}>
              Get in touch
            </Button>
          </Box>
        )}
      </Container>

      {/* Gallery */}
      <Box sx={{ py: 12, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <MotionList
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}
          >
            {images.slice(0, 6).map((img, idx) => (
              <MotionItem key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: { xs: 120, md: 240 },
                    objectFit: 'cover',
                    display: 'block',
                    cursor: 'pointer',
                    transition: 'opacity 0.3s ease',
                    '&:hover': { opacity: 0.78 },
                  }}
                />
              </MotionItem>
            ))}
          </MotionList>
        </Container>
      </Box>

      {/* Amenities */}
      {library.amenities?.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <Reveal y={12} duration={0.5}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, display: 'block', mb: 4 }}>
              Amenities
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {library.amenities.map((amenity, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 0.65 },
                  }}
                >
                  <Box sx={{ color: accentColor }}>
                    {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 20 } })}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 400 }}>
                    {amenity}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Container>
      )}

      {/* Map & Contact */}
      <Box sx={{ borderTop: '1px solid #eee', py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Reveal y={12} duration={0.5}>
                <Box sx={{ height: 300, bgcolor: '#f5f5f5' }}>
                  {library.lat && library.lng ? (
                    <iframe
                      title="Location"
                      width="100%"
                      height="100%"
                      style={{ border: 0, filter: 'grayscale(100%)' }}
                      loading="lazy"
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">Map unavailable</Typography>
                    </Box>
                  )}
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={6}>
              <Reveal delay={0.12} y={12} duration={0.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
                  Contact
                </Typography>
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn sx={{ color: 'text.disabled', fontSize: 18, mt: 0.25 }} />
                    <Typography variant="body1">{library.address}</Typography>
                  </Box>
                  {library.contact_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ color: 'text.disabled', fontSize: 18 }} />
                      <Typography variant="body1">
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  )}
                  {library.contact_email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: 'text.disabled', fontSize: 18 }} />
                      <Typography variant="body1">
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => onBookNow()}
                  sx={{
                    mt: 4,
                    borderColor: 'black',
                    color: 'black',
                    borderRadius: 0,
                    px: 4,
                    transition: 'background-color 0.22s, color 0.22s',
                    '&:hover': { bgcolor: 'black', color: 'white' },
                  }}
                >
                  Book a Seat
                </Button>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: '1px solid #eee', textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'none' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default MinimalTheme;
